import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  motion,
  useScroll,
  useTransform,
} from "framer-motion";
import {
  ArrowRight,
  ArrowDown,
  Handshake,
  MagnifyingGlass,
  UsersThree,
  MapPinLine,
  TreeStructure,
  ChartLineUp,
  Factory,
  Buildings,
  Plant,
  Gear,
  Cpu,
  Lightning,
  Heartbeat,
  Truck,
  ShoppingBag,
  Scales,
  Eye,
  Lock,
  Clock,
  ShieldCheck,
  Leaf,
  ChatCircleText,
  ClipboardText,
  CheckCircle,
  HandCoins,
} from "@phosphor-icons/react";
import SEO, { SITE_URL } from "@/components/site/SEO";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import GlobalMap, { REGIONS } from "@/components/site/GlobalMap";
import PartnershipForm from "@/components/site/PartnershipForm";
import useLenis from "@/hooks/useLenis";

/* ---------- helpers ---------- */
function RevealLines({ lines, className, delay = 0 }) {
  return (
    <span className={className}>
      {lines.map((line, i) => (
        <span key={i} className="reveal-mask">
          <motion.span
            className="block"
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{
              duration: 0.9,
              delay: delay + i * 0.12,
              ease: [0.22, 1, 0.36, 1],
            }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </span>
  );
}

function SectionHead({ overline, title, accent, intro, center, light }) {
  return (
    <div className={`max-w-3xl ${center ? "mx-auto text-center" : ""}`}>
      <div className={`flex items-center gap-3 mb-6 ${center ? "justify-center" : ""}`}>
        {center && <span className="gold-divider" />}
        <span className="overline">{overline}</span>
        <span className="gold-divider" />
      </div>
      <h2
        className={`font-serif-display text-4xl md:text-5xl leading-[1.05] tracking-tight ${
          light ? "text-white" : "text-altin-ink"
        }`}
      >
        {title} {accent && <span className="italic text-altin-gold">{accent}</span>}
      </h2>
      {intro && (
        <p
          className={`mt-6 text-lg font-light leading-relaxed ${
            light ? "text-white/70" : "text-altin-muted"
          }`}
        >
          {intro}
        </p>
      )}
    </div>
  );
}

/* ---------- data ---------- */
const ROLES = [
  { icon: Handshake, label: "Commercial Brokerage" },
  { icon: MagnifyingGlass, label: "Supplier Sourcing" },
  { icon: UsersThree, label: "Business Matchmaking" },
  { icon: MapPinLine, label: "Market Entry" },
  { icon: TreeStructure, label: "Strategic Partnerships" },
  { icon: ChartLineUp, label: "Investment Opportunities" },
];

const REGION_CARDS = [
  { id: "turkiye", name: "Türkiye", note: "Our home base and the strategic gateway bridging European, Middle Eastern and Asian commerce." },
  { id: "europe", name: "Europe", note: "Access to established manufacturers, distributors and regulated markets across the EU and wider Europe." },
  { id: "iraq", name: "Iraq", note: "Reconstruction, materials and consumer-goods flows supported by trusted on-the-ground relationships." },
  { id: "gcc", name: "GCC Countries", note: "High-value investment, infrastructure and trade partnerships across the Gulf economies." },
  { id: "middle-east", name: "Middle East", note: "Cross-border commercial matchmaking connecting suppliers, buyers and investors regionally." },
  { id: "central-asia", name: "Central Asia", note: "Emerging markets with growing demand for equipment, energy and industrial partnerships." },
  { id: "south-asia", name: "South Asia", note: "Large-scale sourcing and manufacturing partnerships across a fast-growing industrial base." },
  { id: "east-asia", name: "East Asia", note: "Technology, manufacturing and supply-chain connections with leading industrial exporters." },
  { id: "africa", name: "Africa", note: "New market entry, commodities and infrastructure opportunities across expanding economies." },
];

const INDUSTRIES = [
  { icon: Factory, label: "Manufacturing" },
  { icon: Buildings, label: "Construction Materials" },
  { icon: Plant, label: "Food & Agriculture" },
  { icon: Gear, label: "Industrial Equipment" },
  { icon: Cpu, label: "Technology" },
  { icon: Lightning, label: "Energy" },
  { icon: Heartbeat, label: "Healthcare" },
  { icon: Truck, label: "Logistics" },
  { icon: ShoppingBag, label: "Consumer Goods" },
];

const NETWORK_STATS = [
  { k: "Global Presence", v: "Growing International Network" },
  { k: "Partners & References", v: "Available Upon Request" },
  { k: "Regional Coverage", v: "Continuously Expanding" },
];

const APPROACH = [
  { t: "Integrity", d: "We act honestly and consistently, honouring our commitments to every client and partner." },
  { t: "Transparency", d: "Clear communication, written records and predictable processes at every stage of cooperation." },
  { t: "Confidentiality", d: "Sensitive commercial information and partner identities are protected under strict NDA-backed protocols." },
  { t: "Long-Term Partnerships", d: "We optimise for relationships that compound value over years — not single transactions." },
  { t: "Professional Representation", d: "We represent our clients with discretion, market intelligence and cultural fluency." },
  { t: "Sustainable Business Growth", d: "We build commercial channels designed to endure, scale and create measurable long-term value." },
];

const STANDARDS = [
  { icon: ShieldCheck, t: "Compliance" },
  { icon: MagnifyingGlass, t: "Due Diligence" },
  { icon: Scales, t: "Business Ethics" },
  { icon: Eye, t: "Risk Awareness" },
  { icon: Leaf, t: "Responsible Business Conduct" },
];

const PROCESS = [
  { icon: ChatCircleText, t: "Consultation" },
  { icon: ClipboardText, t: "Market Assessment" },
  { icon: MagnifyingGlass, t: "Partner Identification" },
  { icon: ShieldCheck, t: "Verification" },
  { icon: UsersThree, t: "Negotiation" },
  { icon: CheckCircle, t: "Transaction Support" },
  { icon: TreeStructure, t: "Long-Term Cooperation" },
];

const MARQUEE = [
  "Türkiye", "Europe", "Iraq", "GCC", "Middle East",
  "Central Asia", "South Asia", "East Asia", "Africa",
];

/* ---------- page ---------- */
export default function GlobalNetwork() {
  useLenis();
  const navigate = useNavigate();
  const [active, setActive] = useState(null);
  const [partnerOpen, setPartnerOpen] = useState(false);
  const heroRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const mapY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const mapScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const heroFade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const goContact = () => navigate("/", { state: { scrollTo: "contact" } });

  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Global Network | Altin Link",
    description:
      "Altin Link's global network connecting businesses across Türkiye, Europe, the Middle East, GCC, Central Asia, South Asia, East Asia and Africa.",
    url: `${SITE_URL}/global-network`,
  };

  return (
    <div data-testid="global-network-page" className="min-h-screen bg-altin-bg overflow-x-clip">
      <SEO
        title="Global Network"
        description="Altin Link's global network — connecting businesses across international markets through trusted commercial relationships. Present across Türkiye, Europe, Iraq, the GCC, the Middle East, Central Asia, South Asia, East Asia and Africa."
        path="/global-network"
        schema={schema}
      />
      <Navbar />

      {/* 1 — HERO */}
      <section
        ref={heroRef}
        data-testid="gn-hero"
        className="relative min-h-[92vh] flex items-center bg-altin-ink overflow-hidden"
      >
        <motion.div
          style={{ y: mapY, scale: mapScale }}
          className="absolute inset-0 opacity-40"
        >
          <div
            className="absolute inset-0 opacity-[0.5]"
            style={{
              backgroundImage:
                "radial-gradient(circle at 1px 1px, rgba(197,160,89,0.35) 1px, transparent 0)",
              backgroundSize: "38px 38px",
            }}
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-[130%] max-w-none -translate-y-4">
              <GlobalMap active={null} />
            </div>
          </div>
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-altin-ink via-altin-ink/50 to-altin-ink/70" />

        <motion.div
          style={{ opacity: heroFade }}
          className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full pt-24"
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="flex items-center gap-3 mb-8"
          >
            <span className="gold-divider" />
            <span className="overline">Altin Link · Worldwide</span>
          </motion.div>

          <h1
            data-testid="gn-hero-title"
            className="font-serif-display text-6xl sm:text-7xl lg:text-8xl leading-[0.95] tracking-tight text-white"
          >
            <RevealLines lines={["Global", "Network"]} delay={0.2} />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="mt-8 text-lg md:text-2xl text-white/75 font-light max-w-2xl leading-relaxed"
          >
            Connecting Businesses Across International Markets Through Trusted
            Commercial Relationships.
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.1 }}
            className="mt-14 flex items-center gap-3 text-white/40 text-xs tracking-[0.3em] uppercase"
          >
            <ArrowDown size={16} weight="light" className="animate-bounce" />
            Explore the network
          </motion.div>
        </motion.div>
      </section>

      {/* Marquee */}
      <div className="gn-marquee-track bg-altin-ink border-y border-white/10 py-6 overflow-hidden">
        <div className="gn-marquee flex whitespace-nowrap w-max">
          {[...MARQUEE, ...MARQUEE].map((m, i) => (
            <span key={i} className="flex items-center">
              <span className="font-serif-display text-3xl md:text-4xl text-white/80 px-8">
                {m}
              </span>
              <span className="text-altin-gold text-2xl">✦</span>
            </span>
          ))}
        </div>
      </div>

      {/* 2 — INTERACTIVE MAP */}
      <section
        id="presence"
        data-testid="gn-map-section"
        className="py-24 md:py-32 bg-altin-ink relative overflow-hidden"
      >
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-4">
              <SectionHead
                light
                overline="Global Presence"
                title="Where we"
                accent="operate"
                intro="Hover a node to trace our commercial reach. From our Istanbul hub, we bridge trade across nine strategic regions."
              />
              <ul className="mt-10 grid grid-cols-2 gap-x-6 gap-y-3">
                {REGIONS.map((r) => (
                  <li key={r.id}>
                    <button
                      data-testid={`gn-region-${r.id}`}
                      onMouseEnter={() => setActive(r.id)}
                      onMouseLeave={() => setActive(null)}
                      className={`flex items-center gap-3 text-sm transition-colors ${
                        active === r.id ? "text-altin-gold" : "text-white/75"
                      }`}
                    >
                      <span
                        className={`w-2 h-2 rounded-full transition-transform ${
                          active === r.id ? "bg-altin-gold scale-150" : "bg-altin-gold/60"
                        }`}
                      />
                      {r.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-8">
              <div className="relative aspect-[1000/520] border border-white/10">
                <GlobalMap active={active} onHover={setActive} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3 — MARKETS WE CONNECT */}
      <section
        id="markets"
        data-testid="gn-markets-section"
        className="py-24 md:py-32 bg-white"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionHead
            overline="Markets We Connect"
            title="One partner,"
            accent="every market"
            intro="Across every region we operate in, Altin Link provides the full spectrum of commercial services — tailored to local realities and international standards."
          />

          {/* the six roles */}
          <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-altin-border border border-altin-border">
            {ROLES.map((r, i) => {
              const Icon = r.icon;
              return (
                <motion.div
                  key={r.label}
                  data-testid={`gn-role-${i}`}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.4, delay: (i % 6) * 0.05 }}
                  className="group bg-white p-6 flex flex-col items-center text-center hover:bg-altin-bg transition-colors"
                >
                  <div className="w-11 h-11 grid place-items-center border border-altin-gold text-altin-gold mb-4 group-hover:bg-altin-gold group-hover:text-white transition-colors">
                    <Icon size={20} weight="light" />
                  </div>
                  <span className="text-xs uppercase tracking-[0.14em] text-altin-ink leading-snug">
                    {r.label}
                  </span>
                </motion.div>
              );
            })}
          </div>

          {/* region cards */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-altin-border border border-altin-border">
            {REGION_CARDS.map((r, i) => (
              <motion.div
                key={r.id}
                data-testid={`gn-market-card-${i}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
                className="group bg-white p-8 relative hover:bg-altin-bg transition-colors duration-300"
              >
                <div className="text-xs tracking-[0.25em] uppercase text-altin-gold mb-3">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="font-serif-display text-2xl text-altin-ink tracking-tight">
                  {r.name}
                </h3>
                <p className="mt-3 text-sm text-altin-muted leading-relaxed">
                  {r.note}
                </p>
                <div className="absolute bottom-0 left-0 h-0.5 w-0 bg-altin-gold transition-all duration-500 group-hover:w-full" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 4 — INDUSTRIES */}
      <section
        id="industries"
        data-testid="gn-industries-section"
        className="py-24 md:py-32 bg-altin-bg"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionHead
            overline="Industries We Serve"
            title="Expertise across"
            accent="key sectors"
          />
          <div className="mt-14 grid grid-cols-2 md:grid-cols-3 gap-px bg-altin-border border border-altin-border">
            {INDUSTRIES.map((it, i) => {
              const Icon = it.icon;
              return (
                <motion.div
                  key={it.label}
                  data-testid={`gn-industry-${i}`}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.45, delay: (i % 3) * 0.07 }}
                  className="group bg-altin-bg p-10 flex items-center gap-5 hover:bg-white transition-colors duration-300"
                >
                  <div className="w-14 h-14 grid place-items-center border border-altin-gold/50 text-altin-gold shrink-0 group-hover:bg-altin-gold group-hover:text-white transition-colors">
                    <Icon size={26} weight="light" />
                  </div>
                  <span className="font-serif-display text-2xl text-altin-ink tracking-tight">
                    {it.label}
                  </span>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 5 — INTERNATIONAL BUSINESS NETWORK */}
      <section
        data-testid="gn-network-section"
        className="py-24 md:py-32 bg-altin-ink"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionHead
            light
            center
            overline="International Business Network"
            title="A network built on"
            accent="trust"
            intro="We let our relationships speak louder than numbers. Detailed references are shared confidentially during qualified engagements."
          />
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {NETWORK_STATS.map((s, i) => (
              <motion.div
                key={s.k}
                data-testid={`gn-stat-${i}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="bg-altin-ink px-8 py-14 text-center"
              >
                <div className="text-[10px] uppercase tracking-[0.3em] text-altin-gold mb-4">
                  {s.k}
                </div>
                <div className="font-serif-display text-2xl md:text-3xl text-white leading-snug">
                  {s.v}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 6 — OUR BUSINESS APPROACH (manifesto chapters) */}
      <section
        id="approach"
        data-testid="gn-approach-section"
        className="py-24 md:py-32 bg-white"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
            <div className="lg:col-span-4">
              <div className="lg:sticky lg:top-28">
                <SectionHead
                  overline="Our Business Approach"
                  title="How we"
                  accent="conduct business"
                  intro="Six principles that define every engagement — the foundation of the trust our partners place in us."
                />
              </div>
            </div>
            <div className="lg:col-span-8">
              <div className="border-t border-altin-border">
                {APPROACH.map((a, i) => (
                  <motion.div
                    key={a.t}
                    data-testid={`gn-approach-${i}`}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{ duration: 0.5, delay: (i % 3) * 0.06 }}
                    className="group grid grid-cols-12 gap-6 items-baseline py-8 border-b border-altin-border hover:bg-altin-bg transition-colors px-4 -mx-4"
                  >
                    <span className="col-span-2 font-serif-display text-4xl md:text-5xl text-altin-gold/30 group-hover:text-altin-gold transition-colors">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div className="col-span-10">
                      <h3 className="font-serif-display text-2xl md:text-3xl text-altin-ink tracking-tight">
                        {a.t}
                      </h3>
                      <p className="mt-2 text-altin-muted leading-relaxed max-w-xl">
                        {a.d}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 7 — INTERNATIONAL STANDARDS */}
      <section
        data-testid="gn-standards-section"
        className="py-24 md:py-32 bg-altin-bg"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionHead
            center
            overline="International Standards"
            title="Held to the highest"
            accent="standards"
          />
          <div className="mt-16 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-px bg-altin-border border border-altin-border">
            {STANDARDS.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={s.t}
                  data-testid={`gn-standard-${i}`}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.45, delay: (i % 5) * 0.07 }}
                  className="group bg-altin-bg py-12 px-6 flex flex-col items-center text-center hover:bg-white transition-colors"
                >
                  <div className="w-14 h-14 rounded-full grid place-items-center border border-altin-gold/60 text-altin-gold mb-4 group-hover:bg-altin-gold group-hover:text-white transition-colors">
                    <Icon size={24} weight="light" />
                  </div>
                  <span className="font-serif-display text-lg text-altin-ink tracking-tight leading-snug">
                    {s.t}
                  </span>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 8 — PARTNERSHIP PROCESS */}
      <section
        id="process"
        data-testid="gn-process-section"
        className="py-24 md:py-32 bg-white"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionHead
            overline="Partnership Process"
            title="From first contact to"
            accent="lasting cooperation"
          />
          <div className="mt-20 relative">
            <div className="hidden lg:block absolute left-0 right-0 top-[38px] h-px bg-altin-border" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-10 lg:gap-4">
              {PROCESS.map((s, i) => {
                const Icon = s.icon;
                return (
                  <motion.div
                    key={s.t}
                    data-testid={`gn-process-${i}`}
                    initial={{ opacity: 0, y: 22 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{ duration: 0.5, delay: i * 0.08 }}
                    className="relative flex flex-col items-center text-center lg:items-start lg:text-left"
                  >
                    <div className="relative z-10 w-[76px] h-[76px] grid place-items-center bg-white border border-altin-border group hover:border-altin-gold transition-colors">
                      <Icon size={26} weight="light" className="text-altin-ink" />
                    </div>
                    <div className="mt-5">
                      <div className="text-[10px] tracking-[0.3em] uppercase text-altin-gold mb-1">
                        {String(i + 1).padStart(2, "0")}
                      </div>
                      <h3 className="font-serif-display text-lg text-altin-ink tracking-tight leading-snug">
                        {s.t}
                      </h3>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* 9 — CTA */}
      <section
        data-testid="gn-cta-section"
        className="relative py-28 md:py-36 bg-altin-ink overflow-hidden"
      >
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, #C5A059 1px, transparent 0)",
            backgroundSize: "40px 40px",
          }}
        />
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7 }}
            className="font-serif-display text-4xl md:text-6xl leading-[1.03] text-white tracking-tight"
          >
            Expand Your Business{" "}
            <span className="italic text-altin-gold">Globally</span> with Altin
            Link.
          </motion.h2>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-12 flex flex-wrap items-center justify-center gap-4"
          >
            <button
              data-testid="gn-cta-contact"
              onClick={goContact}
              className="group inline-flex items-center gap-3 bg-altin-gold text-altin-ink px-8 py-4 text-xs tracking-[0.22em] uppercase hover:bg-altin-goldHover transition-colors duration-300"
            >
              Contact Us
              <ArrowRight weight="light" className="transition-transform group-hover:translate-x-1" />
            </button>
            <button
              type="button"
              data-testid="gn-cta-partner"
              onClick={() => setPartnerOpen(true)}
              className="group inline-flex items-center gap-3 border border-white/30 text-white px-8 py-4 text-xs tracking-[0.22em] uppercase hover:border-altin-gold hover:text-altin-gold transition-colors duration-300 cursor-pointer"
            >
              Become a Partner
            </button>
          </motion.div>
        </div>
      </section>

      <Footer />
      <PartnershipForm open={partnerOpen} onClose={() => setPartnerOpen(false)} />
    </div>
  );
}
